import type { PrintIqKpiSuccess } from '@/types/printiqKpi'

/** KPI block for one segment (TOTAL / EVT / LAN, or a population label). */
export interface OtsKpiBlock {
  label: string
  pct: number
  shipped: number
  onTime: number
  /** Avg lead time for the segment; null/undefined = no data. */
  leadDays?: number | null
  status?: 'good' | 'warn' | 'bad'
  /** Day-over-day OTS % delta (points); undefined when no trend data was returned. */
  delta?: number | null
  /** Previous snapshot date the delta compares against. */
  deltaVsDate?: string | null
}

/** Lead-time chart note; the date mirrors OnTimeShippingService::OTS_WORK_DAY_LEAD_FROM in the API. */
export const OTS_LEAD_DAYS_NOTE = 'Work days (Mon–Fri, no company holidays) for lines started from Sep 28, 2026; calendar days before.'

// Thresholds aligned with OTS page KPI bar: >98 green, 90–98 yellow, <90 red
const GREEN_MIN = 98
const YELLOW_MIN = 90

export function getOtsStatus(block: OtsKpiBlock): 'good' | 'warn' | 'bad' {
  if (block.status != null) return block.status
  if (block.pct === null || block.pct === undefined) return 'good'
  const pct = typeof block.pct === 'number' && !Number.isNaN(block.pct) ? block.pct : 0
  if (pct > GREEN_MIN) return 'good'
  if (pct >= YELLOW_MIN) return 'warn'
  return 'bad'
}

/** Same color conditions as OTS KPI strip and segment cards: green >98%, yellow 90–98%, red <90% */
export function otsStatusClass(block: OtsKpiBlock): string {
  const status = getOtsStatus(block)
  switch (status) {
    case 'good':
      return 'text-green-600 dark:text-green-400'
    case 'warn':
      return 'text-yellow-600 dark:text-yellow-400'
    case 'bad':
      return 'text-red-600 dark:text-red-400'
    default:
      return 'text-text-muted'
  }
}

export function otsBadgeBgClass(block: OtsKpiBlock): string {
  const status = getOtsStatus(block)
  switch (status) {
    case 'good':
      return 'bg-green-500/20'
    case 'warn':
      return 'bg-yellow-500/20'
    case 'bad':
      return 'bg-red-500/20'
    default:
      return 'bg-surface border border-border'
  }
}

export function otsBadgeIconClass(block: OtsKpiBlock): string {
  const status = getOtsStatus(block)
  switch (status) {
    case 'good':
      return 'text-green-600 dark:text-green-400'
    case 'warn':
      return 'text-yellow-600 dark:text-yellow-400'
    case 'bad':
      return 'text-red-600 dark:text-red-400'
    default:
      return 'text-text-muted'
  }
}

/** KPI card icon by color condition: <90% watch, 90–98% check, >98% rocket */
export function otsStatusIconRemix(block: OtsKpiBlock): string {
  const status = getOtsStatus(block)
  switch (status) {
    case 'bad':
      return 'ri-time-line'
    case 'warn':
      return 'ri-checkbox-circle-line'
    default:
      return 'ri-checkbox-circle-line'
  }
}

export function formatOtsPct(value: number | null | undefined): string {
  if (value === null || value === undefined) return 'N/A'
  return typeof value === 'number' && !Number.isNaN(value) ? value.toFixed(2) : '0.00'
}

export function formatOtsInt(value: number): string {
  return typeof value === 'number' && !Number.isNaN(value) ? value.toLocaleString() : '0'
}

/** Lead time as "X.X days" (1 decimal). N/A when null/undefined/non-numeric. */
export function formatOtsLeadDays(value: number | null | undefined): string {
  if (value === null || value === undefined) return 'N/A'
  if (typeof value !== 'number' || Number.isNaN(value)) return 'N/A'
  return `${value.toFixed(1)} days`
}

/** Signed delta as "+ 2.10%" / "- 0.04%" / "0.00%" (two decimals like the KPI %, explicit sign except zero). */
export function formatOtsDelta(delta: number): string {
  const rounded = Math.round(delta * 100) / 100
  if (rounded === 0) return '0.00%'
  const sign = rounded > 0 ? '+' : '-'
  return `${sign} ${Math.abs(rounded).toFixed(2)}%`
}

/** Trend direction for arrow/color: up (green), down (red), flat (0, grey), or null when no data. */
export function otsDeltaTone(delta: number | null | undefined): 'up' | 'down' | 'flat' | null {
  if (delta === null || delta === undefined || Number.isNaN(delta)) return null
  if (delta > 0) return 'up'
  if (delta < 0) return 'down'
  return 'flat'
}

export function kpiToBlocks(kpi: PrintIqKpiSuccess): {
  total: OtsKpiBlock
  evt: OtsKpiBlock
  lan: OtsKpiBlock
} {
  const { trend } = kpi
  return {
    total: {
      label: 'TOTAL',
      pct: kpi.TOTAL_OTS_Pct,
      shipped: kpi.TOTAL_Shipped,
      onTime: kpi.TOTAL_OnTime,
      leadDays: kpi.TOTAL_LeadDays ?? null,
      delta: trend?.TOTAL_OTS_Pct,
      deltaVsDate: trend?.previous_date
    },
    evt: {
      label: 'EVT',
      pct: kpi.EVT_OTS_Pct,
      shipped: kpi.EVT_Shipped,
      onTime: kpi.EVT_OnTime,
      leadDays: kpi.EVT_LeadDays ?? null,
      delta: trend?.EVT_OTS_Pct,
      deltaVsDate: trend?.previous_date
    },
    lan: {
      label: 'LAN',
      pct: kpi.LAN_OTS_Pct,
      shipped: kpi.LAN_Shipped,
      onTime: kpi.LAN_OnTime,
      leadDays: kpi.LAN_LeadDays ?? null,
      delta: trend?.LAN_OTS_Pct,
      deltaVsDate: trend?.previous_date
    }
  }
}

/**
 * Tailwind classes for raw OTS % values (KPI strip, tables, report cards).
 * Matches behavior previously in OnTimeShippingView `pctColorClass`.
 */
export function otsPctTextClass(pct: number | null | undefined): string {
  if (pct === null || pct === undefined) return 'text-green-600 dark:text-green-400'
  if (typeof pct !== 'number' || Number.isNaN(pct)) return 'text-text-muted'
  if (pct > 98) return 'text-green-600 dark:text-green-400'
  if (pct >= 90) return 'text-yellow-600 dark:text-yellow-400'
  return 'text-red-600 dark:text-red-400'
}

/** Background classes for OTS % progress fills; same numeric tiers as otsPctTextClass. Null/invalid → neutral track (bar width is 0% when value is null). */
export function otsPctBarFillClass(pct: number | null | undefined): string {
  if (pct === null || pct === undefined) return 'bg-surface-muted'
  if (typeof pct !== 'number' || Number.isNaN(pct)) return 'bg-surface-muted'
  if (pct > 98) return 'bg-green-600 dark:bg-green-400'
  if (pct >= 90) return 'bg-yellow-600 dark:bg-yellow-400'
  return 'bg-red-600 dark:bg-red-400'
}
